// Arabic instant search: on-demand localized catalog, partial matches and typo tolerance.
export function normalizeSearch(value) {
  return String(value || '').normalize('NFKC').toLocaleLowerCase()
    .replace(/[\u064B-\u065F\u0670\u0640]/g, '')
    .replace(/[أإآٱ]/g, 'ا').replace(/ى/g, 'ي').replace(/ة/g, 'ه')
    .replace(/[٠-٩]/g, digit => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}
const aliases = {
  حفايض: 'حفاض', حفاضات: 'حفاض', حفاضه: 'حفاض', diapers: 'حفاض', diaper: 'حفاض',
  فساتين: 'فستان', dress: 'فستان', dresses: 'فستان', رضاعات: 'رضاعه', رضاعاتي: 'رضاعه',
  مصاصه: 'لهايه', مصاصات: 'لهايه', لهايات: 'لهايه', pacifier: 'لهايه',
  بيبي: 'طفل', اطفال: 'طفل', للاطفال: 'للطفل', baby: 'طفل', babies: 'طفل',
  عربايه: 'عربه', عربات: 'عربه', stroller: 'عربه', strollers: 'عربه',
  بيجامات: 'بيجامه', pajamas: 'بيجامه', pyjamas: 'بيجامه',
  momcozy: 'مومكوزي', مومكوزي: 'مومكوزي', farlin: 'فارلين'
};
function canonicalWords(value) {
  return normalizeSearch(value).replace(/موم\s+كوزي/g, 'مومكوزي')
    .replace(/ملابس\s+(?:ال)?نوم/g, 'بيجامه').split(/\s+/).map(word => aliases[word] || word);
}
function oneEdit(a, b) {
  if (Math.abs(a.length - b.length) > 1) return false;
  if (a.length === b.length) {
    const differences = [];
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) differences.push(i);
    return differences.length <= 1 || (differences.length === 2 && differences[1] === differences[0] + 1 &&
      a[differences[0]] === b[differences[1]] && a[differences[1]] === b[differences[0]]);
  }
  const short = a.length < b.length ? a : b, long = a.length < b.length ? b : a;
  let i = 0, j = 0, skipped = false;
  while (i < short.length && j < long.length) {
    if (short[i] === long[j]) { i++; j++; }
    else if (skipped) return false;
    else { skipped = true; j++; }
  }
  return true;
}
function wordQuality(word, term) {
  const variants = word.startsWith('ال') ? [word, word.slice(2)] : [word];
  if (variants.some(value => value.startsWith(term))) return 2;
  if (term.length < 4 || /\d/.test(term)) return 0;
  return variants.some(value => [term.length - 1, term.length, term.length + 1]
    .some(length => oneEdit(term, value.slice(0, length)))) ? 1 : 0;
}
export function prepareProducts(products) {
  return products.map(product => ({ ...product,
    words: canonicalWords([product.title, product.vendor, product.type, (product.url || '').split('/').pop().split('?')[0]].join(' ')),
    normalizedTitle: normalizeSearch(product.title)
  }));
}
export function matchingProducts(products, query, limit = 6) {
  const normalized = normalizeSearch(query);
  if (!normalized) return [];
  const ignored = new Set(['ابي', 'ابغي', 'ابغى', 'اريد', 'احتاج', 'ابحث', 'عن', 'عندكم']);
  const terms = canonicalWords(normalized).filter(Boolean);
  while (terms.length && ignored.has(terms[0])) terms.shift();
  if (!terms.length) return [];
  const ranked = products.map(product => {
    const qualities = terms.map(term => Math.max(0, ...product.words.map(word => wordQuality(word, term))));
    return { product, qualities, score: (qualities.every(value => value === 2) ? 1000 : 0) +
      (product.available ? 100 : 0) + (product.normalizedTitle === normalized ? 20 : product.normalizedTitle.startsWith(normalized) ? 10 : 0) };
  }).filter(result => result.qualities.every(Boolean))
    .sort((a, b) => b.score - a.score || a.product.normalizedTitle.localeCompare(b.product.normalizedTitle, 'ar'))
    ;
  const exact = ranked.filter(result => result.qualities.every(value => value === 2));
  return (exact.length ? exact : ranked).slice(0, limit).map(result => result.product);
}
export function createCatalogLoader(indexUrl, fetchPage, cache) {
  let pending, current = [];
  const listeners = new Set();
  try {
    const saved = cache?.read();
    if (saved && Date.now() - saved.savedAt < 600000 && Array.isArray(saved.products)) {
      current = prepareProducts(saved.products); pending = Promise.resolve(current);
    }
  } catch (_) {}
  function publish(products) {
    current = prepareProducts([...new Map(products.map(product => [product.id, product])).values()]);
    listeners.forEach(listener => listener(current));
  }
  function load() {
    if (!pending) pending = (async () => {
      const first = await fetchPage(indexUrl, 1);
      if (!Number.isInteger(first.pages) || first.pages < 0 || first.pages > 100) throw new Error('Invalid catalog pagination');
      const products = [...first.products]; publish(products);
      for (let page = 2; page <= first.pages; page += 3) {
        const pages = Array.from({ length: Math.min(3, first.pages - page + 1) }, (_, i) => page + i);
        await Promise.all(pages.map(async number => {
          const chunk = await fetchPage(indexUrl, number); products.push(...chunk.products); publish(products);
        }));
      }
      try { cache?.write({ savedAt: Date.now(), products }); } catch (_) {}
      return current;
    })().catch(error => { pending = undefined; throw error; });
    return pending;
  }
  load.peek = () => current;
  load.subscribe = listener => { listeners.add(listener); return () => listeners.delete(listener); };
  return load;
}
export function productLink(product, doc, currency, fullCard = false) {
  const link = doc.createElement('a');
  link.className = 'mw-instant-product';
  link.href = product.url;
  if (product.image) {
    const image = doc.createElement('img');
    image.src = fullCard ? product.image.replace(/([?&])width=\d+/, '$1width=360') : product.image;
    image.alt = '';
    image.width = image.height = 52;
    image.loading = 'lazy';
    link.appendChild(image);
  }
  const details = doc.createElement('span');
  details.className = 'mw-instant-details';
  const title = doc.createElement('span');
  title.className = 'mw-instant-title';
  title.textContent = product.title;
  details.appendChild(title);
  if (currency && Number.isFinite(product.price)) {
    const money = amount => new Intl.NumberFormat('ar-SA', { style: 'currency', currency }).format(amount / 100);
    const prices = doc.createElement('span');
    prices.className = 'mw-instant-prices';
    const onSale = Number.isFinite(product.compareAtPrice) && product.compareAtPrice > product.price;
    const price = doc.createElement('strong');
    price.className = onSale ? 'mw-instant-sale' : 'mw-instant-price';
    price.textContent = (product.priceVaries ? 'ابتداءً من ' : '') + money(product.price);
    price.setAttribute('aria-label', (onSale ? 'السعر بعد الخصم: ' : 'السعر: ') + price.textContent);
    prices.appendChild(price);
    if (onSale) {
      const original = doc.createElement('s');
      original.className = 'mw-instant-original';
      original.textContent = money(product.compareAtPrice);
      original.setAttribute('aria-label', 'السعر الأصلي: ' + original.textContent);
      prices.appendChild(original);
    }
    details.appendChild(prices);
  }
  link.appendChild(details);
  return link;
}
export function installArabicSearch(PredictiveSearch, config, loadCatalog, doc) {
  const prototype = PredictiveSearch.prototype;
  if (prototype.mwArabicInstantSearch) return;
  prototype.mwArabicInstantSearch = true;
  const originalSearch = prototype.getSearchResults, originalClose = prototype.closeResults, originalOpen = prototype.openSearch;
  const arabic = instance => instance.isArabic;
  function status(instance, message) {
    const node = doc.createElement('div'); node.className = 'mw-instant-status'; node.setAttribute('role', 'status');
    node.textContent = message; instance.predictiveSearchResults.replaceChildren(node);
  }
  prototype.getSearchResults = async function(term) {
    if (!arabic(this)) return originalSearch.call(this, term);
    const request = this.mwSearchRequest = (this.mwSearchRequest || 0) + 1;
    this.removeAttribute('results'); this.setLiveRegionLoadingState(); status(this, config.loading);
    const stillCurrent = () => request === this.mwSearchRequest && this.getQuery() === term && this.contains(doc.activeElement);
    const renderPartial = products => {
      if (!stillCurrent()) return;
      const matches = matchingProducts(products, term);
      if (matches.length) {
        this.predictiveSearchResults.replaceChildren(...matches.map(product => productLink(product, doc))); this.openSearch();
      }
    };
    const unsubscribe = loadCatalog.subscribe?.(renderPartial); renderPartial(loadCatalog.peek?.() || []);
    try {
      const products = await loadCatalog();
      if (!stillCurrent()) return;
      const matches = matchingProducts(products, term);
      if (!matches.length) status(this, config.empty);
      else this.predictiveSearchResults.replaceChildren(...matches.map(product => productLink(product, doc)));
      this.setAttribute('results', 'true'); this.setLiveRegionResults(); this.openSearch();
    } catch (_) {
      if (!stillCurrent()) return;
      status(this, config.error); this.setLiveRegionResults(); this.openSearch();
    } finally { unsubscribe?.(); }
  };
  prototype.closeResults = function(clear) {
    if (arabic(this)) { this.mwSearchRequest = (this.mwSearchRequest || 0) + 1; this.showWrapper.style.display = 'none'; }
    return originalClose.call(this, clear);
  };
  prototype.openSearch = function() {
    if (arabic(this)) this.showWrapper.style.display = 'block';
    return originalOpen.call(this);
  };
}
if (typeof document !== 'undefined') {
  const configNode = document.getElementById('mw-arabic-instant-search-config');
  if (configNode) {
    const config = JSON.parse(configNode.textContent);
    const cacheKey = 'mw-search-v3:' + config.indexUrl + ':' + config.currency;
    const loadCatalog = createCatalogLoader(config.indexUrl, async (base, page) => {
      const url = new URL(base, location.origin); url.searchParams.set('page', page);
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Catalog request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = html.querySelector('[data-mw-search-index]');
      if (!payload) throw new Error('Catalog section missing');
      const chunk = JSON.parse(payload.textContent);
      if (!Array.isArray(chunk.products) || chunk.page !== page) throw new Error('Invalid catalog page');
      return chunk;
    }, {
      read: () => JSON.parse(sessionStorage.getItem(cacheKey) || 'null'),
      write: value => sessionStorage.setItem(cacheKey, JSON.stringify(value))
    });
    const warmCatalog = event => { if (event.target.closest?.('hdt-predictive-search')) loadCatalog().catch(() => {}); };
    document.addEventListener('focusin', warmCatalog);
    document.addEventListener('pointerdown', warmCatalog, { passive: true });
    document.addEventListener('input', event => {
      const widget = event.target.closest?.('hdt-predictive-search');
      if (!event.isComposing && widget && loadCatalog.peek().length && typeof widget.onChange === 'function') widget.onChange();
    });
    const configureForms = () => {
      document.querySelectorAll('hdt-predictive-search form').forEach(form => {
        if (form.querySelector('input[name="view"]')) return;
        const view = document.createElement('input'); view.type = 'hidden'; view.name = 'view'; view.value = 'mw-instant'; form.appendChild(view);
      });
    };
    configureForms(); document.addEventListener('shopify:section:load', configureForms);
    const page = document.querySelector('[data-mw-search-page]');
    if (page) {
      const summary = page.querySelector('[data-mw-search-summary]'), grid = page.querySelector('[data-mw-search-grid]'), more = page.querySelector('[data-mw-search-more]');
      let matches = [], shown = 24;
      function render() {
        grid.replaceChildren(...matches.slice(0, shown).map(product => productLink(product, document, config.currency, true))); more.hidden = shown >= matches.length;
      }
      more.addEventListener('click', () => { shown += 24; render(); });
      loadCatalog().then(products => {
        matches = matchingProducts(products, page.dataset.query, products.length);
        summary.textContent = matches.length ? 'عدد المنتجات: ' + matches.length : config.empty; render();
      }).catch(() => { summary.textContent = config.error; });
    }
    customElements.whenDefined('hdt-predictive-search').then(() => {
      installArabicSearch(customElements.get('hdt-predictive-search'), config, loadCatalog, document);
    });
  }
}
